import { ChatModel as ChatApiClient } from '@/http/models/Chat/Chat'
import { toast } from 'sonner'

import { useCallback, useState } from 'react'

import { ChatModel } from '@/types/model/Chat'

export const useFetchChatTranscript = () => {
  const [messages, setMessages] = useState<ChatModel.ChatMessage[]>([])
  const [hasMore, setHasMore] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)

  const fetchTranscript = useCallback(async (sessionId: string) => {
    setIsLoading(true)

    try {
      const response = await ChatApiClient.getTranscript({ params: { sessionId } })

      if (!response.success || !response.data) {
        throw new Error(response.error?.data?.message || 'Failed to load conversation')
      }

      setMessages(response.data.result)
      setHasMore(response.data.has_more)
      return response.data.result
    } catch (err: any) {
      toast.error(err.message || 'Failed to load conversation')
      setMessages([])
      setHasMore(false)
      return null
    } finally {
      setIsLoading(false)
    }
  }, [])

  const loadOlder = useCallback(
    async (sessionId: string) => {
      if (isLoadingMore || !messages.length) return

      setIsLoadingMore(true)
      try {
        const response = await ChatApiClient.getTranscript({
          params: { sessionId },
          query: { before_id: messages[0].id },
        })

        if (!response.success || !response.data) {
          throw new Error(response.error?.data?.message || 'Failed to load older messages')
        }

        setMessages((prev) => [...response.data!.result, ...prev])
        setHasMore(response.data.has_more)
      } catch (err: any) {
        toast.error(err.message || 'Failed to load older messages')
      } finally {
        setIsLoadingMore(false)
      }
    },
    [messages, isLoadingMore]
  )

  const reset = useCallback(() => {
    setMessages([])
    setHasMore(false)
  }, [])

  return {
    messages,
    hasMore,
    isLoading,
    isLoadingMore,
    fetchTranscript,
    loadOlder,
    reset,
  }
}
